import Vue, { VueConstructor } from 'vue';
import IStorage from './istorage';
import _ from '../../utils';

export default class StorageFactory implements IStorage {
  storage: Storage;
  app: VueConstructor;
  storageName: string;
  namespace: string;

  constructor(app: VueConstructor, storage: Storage, storageNamespace: string, storageName: string = 'storage') {
    this.namespace = storageNamespace;
    this.storage = storage;
    this.app = app;
    this.storageName = storageName;
  }

  isSupported() {
    try {
      const test = 'testStorageSupport';
      try {
        this.storage.setItem(test, test);
        this.storage.removeItem(test);
        return true;
      } catch (e) {
        return false;
      }
    } catch (e) {
      return false;
    }
  }
  set(name: string, value: any) {
    const namespacedName = `${this.namespace}.${name}`;
    const jsonString = JSON.stringify(value);
    try {
      this.storage.setItem(namespacedName, jsonString);
      this.app.$eventbus.emit(`storage/update/${namespacedName}`, value);
    } catch (e) {
      try {
        Vue.$log.warning(e);
        Vue.$log.warning(`Unable to set value '${value}' for ${namespacedName} in ${this.storageName}, fallback to cookies`);
        this.app.$cookie.set(namespacedName, jsonString, 99999);
        this.app.$eventbus.emit(`storage/update/${namespacedName}`, value);
      } catch (eTwo) {
        Vue.$log.error(eTwo);
      }
    }
  }
  get<T>(name: string): T | null {
    const namespacedName = `${this.namespace}.${name}`;
    let jsonString: string | null = '';
    try {
      jsonString = this.storage.getItem(namespacedName);
      if (jsonString == null) {
        return null;
      }
    } catch (e) {
      try {
        Vue.$log.warning(e);
        Vue.$log.warning(`Unable to get value for ${namespacedName} in ${this.storageName}, fallback to cookies`);
        jsonString = this.app.$cookie.get(namespacedName);
        if (jsonString == null) {
          Vue.$log.error(`Unable to get value for ${namespacedName} from cookie`);
          return null;
        }
        const parsed: T = JSON.parse(jsonString);
        return parsed;
      } catch (eTwo) {
        Vue.$log.error(eTwo);
      }
    }

    try {
      if (jsonString !== null) {
        if (jsonString === 'undefined') {
          // If it was stored as undefined
          // Then T can be undefined, so cast to any is legit
          return undefined as any;
        }
        if (jsonString === 'null') {
          return null;
        }
        const parsed: T = JSON.parse(jsonString);
        return parsed;
      }
    } catch (exception) {
      Vue.$log.error(`[${this.storageName}] Unable to parse JSON for ${namespacedName}\n\nexception: ${exception}\n\nvalue: ${jsonString}`);
    }
    return null;
  }
  remove(name: string) {
    const namespacedName = `${this.namespace}.${name}`;
    try {
      this.storage.removeItem(namespacedName);
    } catch (e) {
      try {
        Vue.$log.warning(`Unable to remove value for ${namespacedName} in ${this.storageName}, fallback to cookies`);
        this.app.$cookie.remove(namespacedName);
      } catch (eTwo) {
        Vue.$log.error(eTwo);
      }
    }
  }
  clear() {
    try {
      this.storage.clear();
      this.app.$eventbus.emit('storage/clear');
    } catch (e) {
      Vue.$log.error(`Unable to clear ${this.storageName}: ${e}`);
    }
  }
  get array() {
    const thisStorage = (this.app as any)[`$${this.storageName}`] as IStorage;
    return {
      includes(name: string, value: any) {
        const array = thisStorage.get<Array<any>>(name);
        if (!array) {
          return false;
        }
        return array.includes(value);
      },
      push(name: string, value: any) {
        let array = thisStorage.get<Array<any>>(name);
        if (_.isNil(array)) {
          array = [];
        }
        array!.push(value);
        thisStorage.set(name, array);
      },
      pop(name: string) {
        const array = thisStorage.get<Array<any>>(name);
        // Throws error if null on purpose
        const value = array!.pop();
        thisStorage.set(name, array);
        return value;
      },
      remove(name: string, value: any) {
        const array = thisStorage.get<Array<any>>(name);
        // Throws error if null on purpose
        const filtered = array!.filter((arrayValue: any) => arrayValue !== value);
        thisStorage.set(name, filtered);
        return filtered;
      },
      getByProp<T>(name: string, propName: string, value: any) {
        const array = thisStorage.get<Array<T>>(name);
        // Throws error if null on purpose
        return array!.find((arrayValue: any) => arrayValue[propName] === value);
      },
    };
  }
}
